import { FunctionComponent, useCallback, useEffect, useState } from 'react';
import { TextField, TextFieldProps } from '@shopify/polaris';
import { toNumber } from '@utils/sanitize';
import styled from 'styled-components';

export const FieldLabel = styled.div<{
  smallLabel?: boolean;
}>`
  font-weight: 600;
  font-size: ${(props) => (props.smallLabel ? undefined : '17px')};
`;

export const InputContainer = styled.div`
  max-width: 200px;
`;

export const LargeInputContainer = styled.div`
  max-width: 500px;
`;

export type InputProps = Omit<TextFieldProps, 'autoComplete'> & {
  autoComplete?: string;
  smallLabel?: boolean;
  transform?(value: string | number): string | number;
};

const Input: FunctionComponent<InputProps> = (props) => {
  const {
    autoComplete,
    onChange,
    smallLabel,
    transform,
    type,
    value,
    ...inputProps
  } = props;
  const [textFieldValue, setTextFieldValue] = useState(value);

  const sanitizeTo = useCallback(
    (value: string) => {
      let result = value;
      if (type === 'number') {
        result = toNumber(value);
      }
      return result;
    },
    [type],
  );

  const handleTextFieldChange = useCallback(
    (value, id) => {
      const result = sanitizeTo(transform ? transform(value) : value);
      setTextFieldValue(result);
      if (onChange) {
        onChange(result, id);
      }
    },
    [onChange, sanitizeTo, transform],
  );

  useEffect(() => {
    setTextFieldValue(value);
  }, [value]);

  return (
    <TextField
      {...inputProps}
      autoComplete={autoComplete ?? 'off'}
      label={
        <FieldLabel smallLabel={smallLabel}>{inputProps.label}</FieldLabel>
      }
      type={type}
      value={textFieldValue}
      onChange={handleTextFieldChange}
    />
  );
};

export default Input;
